<template>
    <div class="forecast-advanced-settings">
        <header class="settings-header">
            <h1>{{ $t('production.advanced.title') }}</h1>
        </header>

        <div class="settings-body">
            <div class="type-options" role="radiogroup" :aria-label="$t('production.advanced.title')">
                <section v-for="option in typeOptions" :key="option.value" class="type-card" :class="{ selected: type === option.value }">
                    <button type="button" class="type-selector" role="radio" :aria-checked="type === option.value" @click="type = option.value">
                        <span class="radio"></span>
                        <span><strong>{{ option.title }}</strong><small>{{ option.description }}</small></span>
                    </button>

                    <div v-if="type === option.value" class="expanded-content">
                        <section class="settings-section">
                            <h2>{{ $t('production.advanced.time_period') }}</h2>
                            <label class="period-choice" :class="{ selected: allDay }"><input type="radio" v-model="allDay" :value="true">{{ $t('production.advanced.all_day') }}</label>
                            <div v-if="allDay" class="timeframe all-day">
                                <label>{{ $t('production.advanced.percentage') }}</label>
                                <button type="button" class="field percentage" @click="openPercentage(0)">{{ percentageLabel(periods[0].percentage) }} <i class="i-andy-edit" aria-hidden="true"></i></button>
                            </div>
                            <label class="period-choice" :class="{ selected: !allDay }"><input type="radio" v-model="allDay" :value="false">{{ $t('production.advanced.certain_hours') }}</label>

                            <template v-if="!allDay">
                                <button type="button" class="add-interval" @click="addInterval"><i class="i-andy-add" aria-hidden="true"></i> {{ $t('production.advanced.add_interval') }}</button>
                                <div v-for="(period, index) in periods" :key="index" class="timeframe interval">
                                    <label>
                                        {{ $t('production.advanced.start_time') }}
                                        <select v-model="period.start" class="field">
                                            <option value="" disabled>{{ TIME_PLACEHOLDER }}</option>
                                            <option v-for="time in timeOptions" :key="time" :value="time">{{ time }}</option>
                                        </select>
                                    </label>
                                    <label>
                                        {{ $t('production.advanced.end_time') }}
                                        <select v-model="period.end" class="field">
                                            <option value="" disabled>{{ TIME_PLACEHOLDER }}</option>
                                            <option v-for="time in timeOptions" :key="time" :value="time">{{ time }}</option>
                                        </select>
                                    </label>
                                    <label class="percentage-label">
                                        {{ $t('production.advanced.percentage_short') }}
                                        <button type="button" class="field percentage" @click="openPercentage(index)">{{ percentageLabel(period.percentage) }} <i class="i-andy-edit" aria-hidden="true"></i></button>
                                    </label>
                                    <button v-if="index > 0" type="button" class="remove" :aria-label="$t('production.advanced.remove_interval')" @click="periods.splice(index, 1)"><i class="i-andy-close" aria-hidden="true"></i></button>
                                </div>
                            </template>
                        </section>

                        <section v-if="option.value === 'pmix'" class="settings-section products-section">
                            <h2>{{ $t('production.advanced.products') }} <span v-if="!allProducts && selectedProducts.length">({{ selectedProducts.length }})</span></h2>
                            <label class="period-choice" :class="{ selected: allProducts }"><input type="radio" v-model="allProducts" :value="true">{{ $t('production.advanced.all_products') }}</label>
                            <label class="period-choice" :class="{ selected: !allProducts }"><input type="radio" v-model="allProducts" :value="false">{{ $t('production.advanced.selected_products') }}</label>
                            <template v-if="!allProducts">
                                <input v-model.trim="search" type="search" class="field search" :placeholder="$t('production.advanced.search_products')" :aria-label="$t('production.advanced.search_products')">
                                <div class="product-list">
                                    <label v-for="product in filteredProducts" :key="product.id" class="product-option">
                                        <input type="checkbox" :value="product.id" v-model="selectedProducts">
                                        <span>{{ product.name }}</span>
                                    </label>
                                    <p v-if="!filteredProducts.length">{{ $t('production.advanced.no_products') }}</p>
                                </div>
                            </template>
                        </section>
                    </div>
                </section>
            </div>
        </div>

        <footer class="settings-footer">
            <p v-if="validationMessage" class="validation-message" role="alert">{{ validationMessage }}</p>
            <div class="footer-actions">
                <button type="button" class="btn cancel" :disabled="saving" @click="$emit('back')">{{ $t('production.advanced.cancel') }}</button>
                <button type="button" class="btn confirm" :disabled="!canConfirm || saving" @click="confirm">{{ $t('production.advanced.confirm') }}</button>
            </div>
        </footer>

    </div>
</template>

<script>
const TIME_PLACEHOLDER = '--:--'
const PERCENTAGE_PLACEHOLDER = '--%'

const emptyPeriod = () => ({ start: '', end: '', percentage: null })

export default {
    name: 'ForecastAdvancedSettings',
    props: {
        products: { type: Array, default: () => [] },
        settings: { type: Object, default: null },
        saving: { type: Boolean, default: false }
    },
    data() {
        return {
            TIME_PLACEHOLDER,
            type: null,
            allDay: true,
            periods: [emptyPeriod()],
            allProducts: true,
            selectedProducts: [],
            search: ''
        }
    },
    computed: {
        typeOptions() {
            return [
                { value: 'sales', title: this.$t('production.advanced.sales'), description: this.$t('production.advanced.sales_description') },
                { value: 'pmix', title: this.$t('production.advanced.pmix'), description: this.$t('production.advanced.pmix_description') }
            ]
        },
        timeOptions() {
            return Array.from({ length: 48 }, (_, index) => `${String(Math.floor(index / 2)).padStart(2, '0')}:${index % 2 ? '30' : '00'}`)
        },
        filteredProducts() {
            const query = this.search.toLocaleLowerCase()
            return this.products
                .filter(product => product.status && product.name.toLocaleLowerCase().includes(query))
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        },
        validationMessage() {
            if (!this.type) return this.$t('production.advanced.select_type')
            const periods = this.allDay ? this.periods.slice(0, 1) : this.periods
            if (periods.some(period => period.percentage === null || !Number.isFinite(Number(period.percentage)) || Number(period.percentage) < -100)) {
                return this.$t('production.advanced.invalid_percentage')
            }
            if (!this.allDay) {
                const occupied = new Set()
                for (const period of periods) {
                    const start = this.timeOptions.indexOf(period.start)
                    const end = this.timeOptions.indexOf(period.end)
                    if (start < 0 || end < 0 || start === end) return this.$t('production.advanced.invalid_interval')
                    for (let slot = 0; slot < 48; slot++) {
                        const inside = start < end ? slot >= start && slot < end : slot >= start || slot < end
                        if (inside) {
                            if (occupied.has(slot)) return this.$t('production.advanced.overlap')
                            occupied.add(slot)
                        }
                    }
                }
            }
            if (this.type === 'pmix' && !this.allProducts && !this.selectedProducts.length) {
                return this.$t('production.advanced.select_product')
            }
            return ''
        },
        canConfirm() {
            return !this.validationMessage
        },
        percentageModalTitle() {
            const adjustmentType = this.type === 'sales' ? this.$t('production.advanced.sales') : this.$t('production.advanced.pmix')
            return this.$t('production.advanced.percentage_modal', { type: adjustmentType })
        }
    },
    methods: {
        addInterval() {
            this.periods.push(emptyPeriod())
        },
        percentageLabel(value) {
            return value === null ? PERCENTAGE_PLACEHOLDER : `${Number(value) > 0 ? '+' : ''}${value}%`
        },
        openPercentage(index) {
            const component = this
            this.$popup.numeric({
                title: this.percentageModalTitle,
                inputVal: this.periods[index].percentage === null ? '0' : String(this.periods[index].percentage),
                hasNegative: true,
                decimal: true,
                textCancel: this.$t('production.advanced.cancel'),
                textSave: this.$t('production.advanced.confirm'),
                callSave(context) {
                    const percentage = Number(context.inputVal)
                    if (Number.isFinite(percentage) && percentage >= -100) {
                        component.$set(component.periods[index], 'percentage', percentage)
                    }
                }
            })
        },
        confirm() {
            if (!this.canConfirm) return
            this.$emit('confirm', {
                type: this.type,
                all_day: this.allDay,
                periods: (this.allDay ? this.periods.slice(0, 1) : this.periods).map(period => ({
                    start: this.allDay ? null : period.start,
                    end: this.allDay ? null : period.end,
                    percentage: Number(period.percentage)
                })),
                all_products: this.type === 'sales' || this.allProducts,
                products: this.type === 'pmix' && !this.allProducts ? this.selectedProducts : []
            })
        },
        resetForm() {
            this.type = null
            this.allDay = true
            this.periods = [emptyPeriod()]
            this.allProducts = true
            this.selectedProducts = []
            this.search = ''
        },
        hydrate(value) {
            if (!value) {
                this.resetForm()
                return
            }
            this.type = value.type
            this.allDay = value.all_day === true
            this.periods = Array.isArray(value.periods) && value.periods.length
                ? value.periods.map(period => ({ start: period.start || '', end: period.end || '', percentage: period.percentage }))
                : [emptyPeriod()]
            this.allProducts = value.all_products === true
            this.selectedProducts = Array.isArray(value.products) ? value.products.slice() : []
            this.search = ''
        }
    },
    watch: {
        settings: {
            immediate: true,
            handler(value) {
                if (!value && this.type) {
                    return
                }
                this.hydrate(value)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.forecast-advanced-settings {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    color: #374673;
}
.settings-header { position: relative; display: flex; align-items: center; justify-content: center; min-height: 54px; padding: 0 8px 14px; }
.settings-header h1 { margin: 0; color: #222; font-family: $text-bold; font-size: 22px; }
.settings-body { padding: 0 8px 16px; overflow-y: auto; flex: 1; white-space: normal; }
.type-options { display: flex; flex-direction: column; gap: 14px; }
.type-card { width: 100%; background: #fff; border: 1px solid transparent; border-radius: 5px; color: inherit; overflow: hidden; }
.type-card.selected { border-color: $color-secondary-300; }
.type-selector { display: flex; align-items: flex-start; gap: 12px; width: 100%; padding: 16px; text-align: left; background: transparent; border: 0; color: inherit; cursor: pointer; }
.type-selector strong, .type-selector small { display: block; }
.type-selector strong { color: #222; font-family: $text-bold; font-size: 16px; }
.type-selector small { margin-top: 2px; color: #666; font-size: 13px; line-height: 1.35; }
.radio { width: 18px; height: 18px; margin-top: 1px; border: 2px solid #929292; border-radius: 50%; flex: none; }
.type-card.selected > .type-selector .radio { border: 5px solid $color-secondary-300; }
.expanded-content { padding: 0 16px 16px; }
.settings-section { margin-top: 8px; }
.settings-section h2 { margin: 0 0 10px; padding: 9px 10px; border-radius: 3px; background: #ededed; color: #333; font-family: $text-bold; font-size: 16px; }
.period-choice { display: flex; align-items: center; gap: 9px; min-height: 42px; margin: 8px 0; padding: 10px 12px; border: 1px solid #f0f0f0; cursor: pointer; }
.period-choice.selected { background: $color-secondary-100; color: #222; font-family: $text-bold; }
.period-choice input { accent-color: $color-secondary-300; }
.timeframe { display: flex; align-items: flex-end; gap: 14px; margin: 8px 0 12px; padding: 0 32px; }
.timeframe label { display: flex; flex: 1 1 240px; flex-direction: column; gap: 6px; color: #555; font-size: 13px; text-transform: uppercase; }
.timeframe.all-day { display: block; }
.timeframe.all-day label { display: block; margin-bottom: 6px; }
.timeframe.all-day .percentage { width: 245px; }
.timeframe .percentage-label { flex: 0 0 130px; }
.field { display: block; width: 100%; min-width: 130px; height: 42px; padding: 8px 12px; border: 1px solid #b9c4d9; border-radius: 4px; background: #fff; color: inherit; text-align: left; }
.percentage { display: flex; align-items: center; justify-content: space-between; cursor: pointer; }
.remove, .add-interval { border: 0; background: transparent; color: $color-secondary-500; cursor: pointer; }
.remove { height: 42px; font-size: 25px; }
.add-interval { display: block; margin: 4px 8px 4px auto; font-family: $text-bold; font-size: 14px; }
.search { width: calc(100% - 64px); max-width: 420px; margin: 12px 32px; }
.product-list { max-height: 230px; margin: 0 32px; overflow-y: auto; border: 1px solid #dce2ee; border-radius: 4px; }
.product-option { display: flex; gap: 10px; padding: 9px 12px; }
.settings-footer { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 16px 24px 4px; }
.validation-message { margin: 0; color: #c0392b; font-size: 14px; text-align: center; }
.footer-actions { display: flex; align-items: center; justify-content: center; gap: 10px; }
.settings-footer .btn { display: inline-flex; align-items: center; justify-content: center; width: 200px; height: 45px; min-height: 45px; margin: 0; padding: 0 12px; border-radius: 3px; font-family: $text-bold; font-size: 16px; line-height: 1; white-space: nowrap; }
.settings-footer .cancel { border: 1px solid $color-neutral-500; background: $color-white; color: $color-black; }
.settings-footer .confirm { border: 1px solid $color-secondary-500; background: $color-secondary-500; color: $color-white; }
.settings-footer .cancel:disabled,
.settings-footer .confirm:disabled { opacity: .55; cursor: not-allowed; }
@media (max-width: 700px) {
    .settings-header h1 { font-size: 18px; }
    .settings-body { padding: 0 0 12px; }
    .timeframe { flex-wrap: wrap; padding: 0 12px; }
    .timeframe label, .timeframe .percentage-label { flex: 1 1 130px; }
    .search { width: calc(100% - 24px); margin: 12px; }
    .product-list { margin: 0 12px; }
}
</style>
